import React, { useRef, useState } from "react";
import Container from '@mui/material/Container';
import { Avatar, Box, Button, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { deepPurple } from "@mui/material/colors";

export function Main(props) {
  const scrollRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const features = [
    {
      title: "Notes",
      description: "Whether you're organizing notes, setting up task lists, capturing audio, building video galleries, or storing images, GeoDoc makes it easy to keep everything connected to the places that matter most to you.",
      image: require("../../static/main_page.jpeg"),
      reverse: true
    },
    {
      title: "Create and Share Diverse Documents",
      description: "Craft notes, to-do lists, audio recordings, video galleries, and image collections effortlessly. Share your points of interest with contacts, allowing collaborative access to documents and interaction between users.",
      image: require("../../static/contacts.jpeg"),
      reverse: false
    },
    {
      title: "Location-Based Triggers",
      description: "Assign documents to specific geo-locations and set a trigger radius. Receive notifications when you arrive at or leave these locations, ensuring you never miss an important reminder.",
      image: require("../../static/location.jpeg"),
      reverse: true
    },
    {
      title: "Interactive Map View",
      description: "View, manage, and share your geo-tagged documents on an interactive map. Easily navigate to your points of interest and keep everyone in the loop by sharing specific points with contacts.",
      image: require("../../static/share.jpeg"),
      reverse: false
    },
    {
      title: "Collaborative & Flexible Management",
      description: "Add, edit, or delete location points and their associated documents with just a few taps. Enjoy the flexibility to collaborate, share, and customize the app to suit your needs.",
      image: require("../../static/task_list.jpeg"),
      reverse: true
    },
    {
      title: "Timely Location Alerts",
      description: "Stay informed with timely alerts based on your location. GeoDoc keeps you organized and connected, wherever you are.",
      image: require("../../static/notification.jpeg"),
      reverse: false
    },
    {
      title: "Secure Data Storage",
      description: "Your data is securely stored, giving you complete control over your documents and locations. GeoDoc respects your privacy, ensuring a safe and reliable experience.",
      image: require("../../static/data_privacy.jpeg"),
      reverse: true
    },
    {
      title: "Enhanced Collaboration",
      description: "GeoDoc takes location-based organization to the next level by enabling collaboration and interactive sharing with friends and colleagues. Whether it's for coordinating tasks, sharing memories, or keeping others informed, GeoDoc makes every point a shared experience.",
      image: require("../../static/collaboration.jpeg"),
      reverse: false
    }
  ];

  const FeatureSection = ({ title, description, image, reverse, index }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <Stack 
        flexDirection={isMobile ? "column" : reverse ? "row-reverse" : "row"} 
        sx={{ 
          height: 'auto', 
          width: '100%', 
          marginTop: isMobile ? 6 : 3,
          position: 'relative',
          transition: 'all 0.3s ease-in-out',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          '&:hover': {
            transform: 'translateY(-4px)',
          }
        }} 
        justifyContent="center" 
        alignItems="center"
        gap={6}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Hover overlay effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isHovered 
              ? 'linear-gradient(135deg, rgba(66, 111, 235, 0.1) 0%, rgba(235, 66, 126, 0.1) 100%)'
              : 'transparent',
            borderRadius: 4,
            transition: 'all 0.3s ease-in-out',
            opacity: isHovered ? 1 : 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
        
        {/* Glow effect */}
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            left: -10,
            right: -10,
            bottom: -10,
            background: isHovered 
              ? 'linear-gradient(135deg, rgba(66, 111, 235, 0.15) 0%, rgba(235, 66, 126, 0.15) 100%)'
              : 'transparent',
            borderRadius: 8,
            filter: 'blur(10px)',
            transition: 'all 0.3s ease-in-out',
            opacity: isHovered ? 1 : 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />

        <Box 
          sx={{ 
            width: isMobile ? '100%' : '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: isMobile ? 1 : 8,
            position: 'relative',
            zIndex: 2,
            transition: 'all 0.3s ease-in-out',
            transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          }}
        >
          <img 
            src={image} 
            alt={title}
            style={{
              objectFit: 'contain',
              height: 'auto',
              width: '100%',
              maxWidth: 500,
              borderRadius: 12,
              boxShadow: isHovered 
                ? '0 12px 40px rgba(66, 111, 235, 0.3), 0 8px 32px rgba(0,0,0,0.3)'
                : '0 8px 32px rgba(0,0,0,0.3)',
              transition: 'all 0.3s ease-in-out',
              filter: isHovered ? 'brightness(1.05)' : 'brightness(1)',
              
            }}
            loading="lazy"
          />
        </Box>
        <Box 
          sx={{ 
            height: 'auto', 
            width: isMobile ? '100%' : '50%',
            textAlign: isMobile ? 'center' : 'left',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              color: 'white', 
              fontSize: isMobile ? '2rem' : '2.5rem',
              fontWeight: 700,
              marginBottom: 3,
              background: isHovered 
                ? 'linear-gradient(45deg, #426feb, #eb427e, #426feb)'
                : 'linear-gradient(45deg, #426feb, #eb427e)',
              backgroundSize: isHovered ? '200% 200%' : '100% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              animation: isHovered ? 'gradientShift 2s ease infinite' : 'none',
              padding: 2,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: isHovered ? '#ffffff' : '#e0e0e0', 
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              lineHeight: 1.6,
              opacity: isHovered ? 1 : 0.9,
              padding: 2,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%' }}>
      {/* Add CSS animation for gradient shift */}
      <style>
        {`
          @keyframes gradientShift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
        `}
      </style>
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          background: "linear-gradient(#091428, #00120B)", 
          minHeight: '100vh',
        }} 
        disableGutters={false}
      >
        {/* Header */}
        <Stack 
          direction="row" 
          sx={{ 
            height: 70, 
            background: 'rgba(9, 20, 40, 0.95)',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
            px: 3
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', flex: 1 }}>
            <img 
              src={require("../../static/logo192.png")} 
              alt="GeoDoc Logo"
              style={{ height: 40, width: 40 }}
            />
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'white', 
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: 700,
                marginLeft: 2
              }}
            >
              GeoDoc
            </Typography>
          </Stack>
          <Stack 
            direction="row" 
            sx={{ 
              alignItems: 'center',
              justifyContent: 'flex-end',
              flex: 1
            }}
          >
            <button 
              className="btn-solid-lg secondary" 
              onClick={() => {
                scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label="Download GeoDoc App"
            >
              Download
            </button>
          </Stack>
        </Stack>

        {/* Hero Section */}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          sx={{ 
            height: 'auto', 
            width: '100%', 
          }}
          gap={6}
          justifyContent="center"
          alignItems="center"
        >
          <Box sx={{ 
            width: isMobile ? '100%' : '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2
          }}>
            <img 
              src={require("../../static/pointer.jpeg")} 
              alt="GeoDoc App Interface"
              style={{
                objectFit: 'contain',
                height: 'auto',
                width: '100%',
                maxWidth: 500,
                borderRadius: 16,
                boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
              }}
            />
          </Box>
          <Stack 
            sx={{ 
              height: 'auto', 
              width: isMobile ? '100%' : '50%',
              padding: 3
            }} 
            alignItems={isMobile ? "center" : "flex-start"} 
            justifyContent="center"
            gap={3}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white', 
                fontSize: isMobile ? '2.5rem' : '3.5rem',
                fontWeight: 800,
                textAlign: isMobile ? 'center' : 'left',
                lineHeight: 1.2,
                background: 'linear-gradient(45deg, #ffffff, #426feb)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                paddingRight: 3
              }}
            >
              Find Your Secret Spots
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#e0e0e0', 
                fontSize: isMobile ? '1.1rem' : '1.5rem',
                textAlign: isMobile ? 'center' : 'left',
                lineHeight: 1.6,
                opacity: 0.9,
                padding: isMobile ? 1 : 2,
                paddingRight: 3
              }}
            >
              Transform your everyday tasks into seamless, location-based experiences with GeoDoc, the ultimate tool for creating and managing documents tied to specific geographic locations.
            </Typography>
          </Stack>
        </Stack>

        {/* Features Sections */}
        <Stack flexDirection="column" sx={{ 
          height: 'auto', 
          width: '100%', 
        }}>
          {features.map((feature, index) => (
            <FeatureSection key={index} {...feature} index={index} />
          ))}
        </Stack>

        {/* Download Section */}
        <Stack 
          ref={scrollRef} 
          sx={{ 
            width: '100%', 
            justifyContent: 'center', 
            alignItems: 'center', 
            marginTop: 15,
            paddingBottom: 10,
          }} 
          direction={isMobile ? "column" : "row"}
          gap={4}
        >
          <a 
            href="https://play.google.com/store/apps/details?id=com.geoplannerexpert" 
            target="_blank" 
            className="market-btn apple-btn" 
            role="button" 
            rel="noopener noreferrer"
            aria-label="Download GeoDoc on App Store"
          >
            <span className="market-button-subtitle">Download on the</span>
            <span className="market-button-title">App Store</span>
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=com.geoplannerexpert" 
            target="_blank" 
            className="market-btn google-btn" 
            role="button" 
            rel="noopener noreferrer"
            aria-label="Download GeoDoc on Google Play"
          >
            <span className="market-button-subtitle">Download on the</span>
            <span className="market-button-title">Google Play</span>
          </a>
        </Stack>
      </Container>
    </Box>
  );
}